import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { BookUser, Search, Snowflake, Fingerprint } from "lucide-react";
import Nav from "@/components/Nav";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

const THEME_GRAD = {
  aurora: "linear-gradient(135deg,#00F0FF,#7A5CFF,#FF007F)",
  sunset: "linear-gradient(135deg,#FFE600,#FF7A00,#FF007F)",
  matrix: "linear-gradient(135deg,#00FF9D,#00C2FF,#0047FF)",
  molten: "linear-gradient(135deg,#FF007F,#FF4D00,#FFE600)",
};

export default function Directory() {
  const [entries, setEntries] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/directory").then((r) => setEntries(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return entries;
    return entries.filter(
      (e) =>
        e.nickname?.toLowerCase().includes(term) ||
        e.owner_name?.toLowerCase().includes(term) ||
        e.cardholder_name?.toLowerCase().includes(term)
    );
  }, [q, entries]);

  return (
    <div className="min-h-screen bg-[#0A0A0C] relative">
      <div className="pointer-events-none fixed top-0 right-0 w-[600px] h-[600px] rounded-full bg-[#00F0FF] opacity-[0.06] blur-[140px]" />
      <Nav active="directory" />

      <main className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 py-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="font-mono-sig text-[11px] tracking-[0.3em] uppercase text-[#52525B] mb-1">Network Accounts</p>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter flex items-center gap-3">
              <BookUser className="w-8 h-8 text-[#00F0FF]" /> Account Directory
            </h1>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 text-[#52525B] absolute left-4 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="directory-search-input"
              placeholder="Search by nickname or owner"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="bg-white/5 border-white/10 text-white h-12 pl-11 rounded-full"
            />
          </div>
        </div>

        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full border-2 border-[#00F0FF]/20 border-t-[#00F0FF] animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/15 bg-white/[0.02] py-20 text-center text-[#A0A0AB]">
            No accounts found.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-grid">
            {filtered.map((e, i) => (
              <motion.div
                key={`${e.nickname}-${i}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.03, 0.4) }}
                whileHover={{ y: -4 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl hover:border-white/20 transition-colors"
              >
                <div className="flex items-center justify-between mb-4">
                  <span
                    className="w-11 h-11 rounded-xl flex items-center justify-center"
                    style={{ background: THEME_GRAD[e.color_theme] || THEME_GRAD.aurora }}
                  >
                    <Fingerprint className="w-5 h-5 text-white drop-shadow" />
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1">
                      <span className={`w-1.5 h-1.5 rounded-full ${e.online ? "bg-[#00FF9D] animate-pulse" : "bg-[#52525B]"}`} />
                      <span className="font-mono-sig text-[9px] tracking-[0.15em] uppercase text-[#A0A0AB]">{e.online ? `Online · ${e.online_count}` : "Offline"}</span>
                    </span>
                    {e.frozen && (
                      <Badge className="bg-[#00C2FF]/15 text-[#00C2FF] border-0">
                        <Snowflake className="w-3 h-3 mr-1" /> Frozen
                      </Badge>
                    )}
                  </div>
                </div>
                <p data-testid="directory-nickname" className="font-mono-sig text-lg text-[#00F0FF] tracking-wide mb-1 break-all">
                  {e.nickname || "—"}
                </p>
                <p className="text-white font-medium">{e.owner_name}</p>
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
                  <span className="font-mono-sig text-[10px] tracking-[0.15em] uppercase text-[#FFE600]">{e.total_uses ?? 0} uses</span>
                  <span className="font-mono-sig text-[11px] text-[#A0A0AB]">•••• {e.last4}</span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
